import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import type { Scene } from "@heroncendre/schema";
import { pickText, resolveAssetUrl, useCoarsePointer, useTourState } from "@heroncendre/player";
import chainesJson from "./chaines.json";
import { construireEspaces, espaceDe, type Espace } from "./espaces.js";
import { etatNavigation, useEtatNavigation } from "./etatNavigation.js";
import { COULEURS, POLICE_TITRE_SPLASH, POLICE_UI_SPLASH } from "./splashStyles.js";
import { BoutonInfos } from "./info/BoutonInfos.js";
import { InvitePlan } from "./InvitePlan.js";
import { ListeDefilante } from "./ListeDefilante.js";
import { SpacePlan } from "./SpacePlan.js";
import { useChangementScene } from "./useChangementScene.js";
import { useObstacleBas } from "./useObstacleBas.js";
import { useReducedMotion } from "./useReducedMotion.js";
import "./sceneNav.css";

/**
 * ============================================================================
 *  SceneNav.tsx — La navigation « carnet » entre les scènes
 * ----------------------------------------------------------------------------
 *  Référence : `NavScenes.dc.html` (variante carnet) du handoff
 *  `design_handoff_navigation_carnet`. Elle remplace le menu en accordéon et le
 *  plan du moteur.
 *
 *   - REPLIÉE : une étiquette « où suis-je » (vignette, espace, nom), en
 *     bas à gauche sur ordinateur ; sur téléphone, une barre collée au bas de
 *     l'écran, pleine largeur — sous le pouce, et sans couper le panorama en
 *     son milieu. Le contrôleur d'ambiances se pose au-dessus d'elle
 *     (`ETIQUETTE_HAUTEUR_MOBILE`). L'en-tête (accueil, logo, langues) reste intact.
 *   - OUVERTE : un panneau à gauche sur ordinateur, une feuille basse sur
 *     téléphone (avec un voile qui la referme) : le PLAN, et sous lui les
 *     scènes. La liste de tous les espaces en accordéon (onglet LISTE) a été
 *     retirée : le plan et ses zones numérotées y suffisent.
 *
 *  Sur ORDINATEUR, le plan montre toujours le plan complet du domaine :
 *  une pastille numérotée par espace (ailleurs, ici, survolé) qui
 *  emmène à sa première scène, et sous le plan les scènes de l'espace courant.
 *  Le téléphone garde la navigation d'espace en espace.
 *
 *  Les scènes d'un espace se rangent par SOUS-ZONE (étage, partie : un
 *  sous-groupe de l'espace), sous un en-tête qui la replie. Celles posées à la
 *  racine de l'espace viennent d'abord, sans en-tête. Une seule sous-zone est
 *  dépliée à la fois : celle où l'on arrive (à chaque changement de scène et à
 *  chaque ouverture) ; en déplier une autre à la main replie la précédente.
 *
 *  Choisir une scène y emmène par un fondu au noir (`useChangementScene`). Sur
 *  téléphone, le panneau se referme ; sur ordinateur il reste ouvert, et ne
 *  disparaît pas pendant le changement de scène : on continue de parcourir. Échap le referme et rend le focus à l'étiquette.
 *
 *  L'état de la navigation (ouverture, espace vu, sous-zone dépliée) vit dans
 *  `etatNavigation`, partagé : sur téléphone, le contrôleur d'ambiances
 *  s'efface sous la feuille ; et le titre d'arrivée (`SceneTitle`) s'en sert.
 *
 *  ── Avec le titre d'arrivée ────────────────────────────────────────────────
 *
 *  La navigation ne bouge pas pendant un changement de scène ni pendant le
 *  titre : ouverte, elle reste ouverte ; repliée, la carte reste là, dans son
 *  état. Le titre se pose à côté (centré), au même bas.
 *
 *  Les espaces sont lus dans le document (`espaces.ts`) : rien de propre au
 *  château ici.
 * ==========================================================================*/

type Cle = keyof typeof chainesJson.fr;

/** Hauteur laissée à l'en-tête au-dessus du panneau ouvert, sur ordinateur. */
const MARGE_ENTETE = 120;
/** Marge du coin bas gauche, sur ordinateur (handoff : 20 px). */
export const MARGE_COIN = 20;
/**
 * Largeur maximale de l'étiquette sur ordinateur. Le contrôleur d'ambiances se
 * centre à sa droite (`--va-gauche` dans `variantSwitcher.css`) : les deux
 * valeurs vont ensemble.
 */
export const ETIQUETTE_LARGEUR = 380;
/** La carte repliée (ordinateur) : sa largeur et sa hauteur, d'où le panneau se déplie. */
const CARTE_LARGEUR = 260;
const CARTE_HAUTEUR = 244;
const DEPLIAGE_OUVERTURE = "width .45s cubic-bezier(.2,.7,.2,1), max-height .45s cubic-bezier(.2,.7,.2,1)";
const DEPLIAGE_FERMETURE = "width .35s ease, max-height .35s ease";
/**
 * Hauteur de la barre sur téléphone, hors zone sûre. Le contrôleur d'ambiances
 * se pose juste au-dessus (`--va-bas` dans `variantSwitcher.css`) : les deux
 * valeurs vont ensemble.
 */
export const ETIQUETTE_HAUTEUR_MOBILE = 64;

export interface SceneNavProps {
  assetBaseUrl: string;
}

const onOuvert = etatNavigation.setOuvert;

export function SceneNav({ assetBaseUrl }: SceneNavProps) {
  const tour = useTourState((s) => s.tour);
  const language = useTourState((s) => s.context.language);
  const sceneId = useTourState((s) => s.currentSceneId);
  const mobile = useCoarsePointer();
  const reduit = useReducedMotion();
  const { aller, noir, duree } = useChangementScene(reduit);
  const ouvert = useEtatNavigation((e) => e.ouvert);
  const vu = useEtatNavigation((e) => e.espaceVu);
  const deployee = useEtatNavigation((e) => e.sousZone);

  const espaces = useMemo(() => construireEspaces(tour), [tour]);
  const courant = espaceDe(espaces, sceneId);
  const setVu = (id: string | undefined) => etatNavigation.setEspaceVu(id ?? null);
  const sousZoneIci = courant?.scenes.find((e) => e.scene.id === sceneId)?.sousZone ?? null;
  useEffect(() => etatNavigation.setSousZone(sousZoneIci), [sousZoneIci, sceneId, ouvert]);
  const etiquette = useRef<HTMLButtonElement>(null);
  const tirer = useTirerPourFermer(() => onOuvert(false));
  const fermer = useRef<HTMLButtonElement>(null);
  const calque = useRef<HTMLDivElement>(null);
  useObstacleBas("navigation", etiquette, mobile && courant !== undefined);

  const t = (cle: Cle, n?: number) => {
    const table = (chainesJson as Record<string, Partial<Record<Cle, string>>>)[language] ?? chainesJson.fr;
    return (table[cle] ?? chainesJson.fr[cle]).replace("{n}", String(n ?? ""));
  };
  const nom = (texte: Scene["title"] | Espace["titre"]) => pickText(texte, language) ?? "";

  // À chaque ouverture, le plan suit la scène courante (on peut ensuite en parcourir d'autres).
  useEffect(() => {
    if (!ouvert) return;
    setVu(courant?.id);
    // Sans `preventScroll`, le navigateur ferait défiler le conteneur pour montrer
    // ce bouton, encore hors écran au début du glissement : la feuille sauterait
    // au lieu de monter.
    fermer.current?.focus({ preventScroll: true });
    // `courant` change avec la scène : seule l'ouverture compte ici.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert]);

  // Échap referme, et rend le focus à l'étiquette.
  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onOuvert(false);
      etiquette.current?.focus();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [ouvert]);

  if (!courant) return null;

  const choisir = (id: string) => {
    if (mobile) onOuvert(false);
    aller(id);
  };

  const sceneCourante = tour.scenes.find((scene) => scene.id === sceneId);
  const espaceVu = espaces.find((e) => e.id === vu) ?? courant;
  const racine = espaces[0]!;

  const ligne = (scene: Scene, numero: number) => {
    const ici = scene.id === sceneId;
    return (
      <button
        key={scene.id}
        type="button"
        className="sn-bouton"
        data-sn-cle={`scene:${scene.id}`}
        data-sn-courant={ici}
        aria-current={ici ? "location" : undefined}
        onClick={() => choisir(scene.id)}
        style={{
          ...ligneStyle,
          background: ici ? COULEURS.creme : "rgba(246,238,227,.04)",
          color: ici ? COULEURS.vert : COULEURS.texteRepos,
          borderColor: ici ? COULEURS.creme : "rgba(246,238,227,.12)",
        }}
      >
        <Vignette scene={scene} assetBaseUrl={assetBaseUrl} largeur={72} hauteur={40} />
        <span style={{ fontSize: 11, fontWeight: 600, color: ici ? "#4A6559" : "#BFB4A1", flex: "none" }}>{numero}</span>
        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={ellipse}>{nom(scene.title)}</span>
          {ici && <span style={{ ...surtitreTexte, color: "#4A6559" }}>{t("navHere")}</span>}
        </span>
      </button>
    );
  };

  /** Les scènes d'un espace : d'abord celles de sa racine, puis chaque sous-zone, repliable. */
  const listeScenes = (espace: Espace) => {
    const numero = (entree: Espace["scenes"][number]) => espace.scenes.indexOf(entree) + 1;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {espace.scenes.filter((e) => !e.sousZone).map((e) => ligne(e.scene, numero(e)))}
        {espace.sousZones.map((zone) => {
          const entrees = espace.scenes.filter((e) => e.sousZone === zone.id);
          const ouverte = deployee === zone.id;
          const ici = entrees.some((e) => e.scene.id === sceneId);
          return (
            <div key={zone.id} style={{ display: "flex", flexDirection: "column" }}>
              <button
                type="button"
                className="sn-bouton"
                data-sn-cle={`sous-zone:${zone.id}`}
                data-sn-courant="false"
                aria-expanded={ouverte}
                onClick={() => etatNavigation.setSousZone(ouverte ? null : zone.id)}
                style={enteteSousZone}
              >
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden style={{ flex: "none", transform: ouverte ? "rotate(90deg)" : "none", transition: "transform .2s" }}>
                  <path d="M9 5l7 7-7 7" />
                </svg>
                <span style={{ flex: 1, minWidth: 0, ...ellipse, textAlign: "left" }}>{nom(zone.titre)}</span>
                {ici && <span aria-hidden style={losange} />}
              </button>
              {/* Dépliage animé : la rangée passe de 0fr à 1fr. Replié, le contenu reste monté
                  mais `visibility: hidden` (après la fermeture) le sort du clavier. */}
              <div
                style={{
                  display: "grid",
                  gridTemplateRows: ouverte ? "1fr" : "0fr",
                  transition: reduit ? "none" : "grid-template-rows .3s ease",
                }}
              >
                <div
                  style={{
                    minHeight: 0,
                    overflow: "hidden",
                    visibility: ouverte ? "visible" : "hidden",
                    transition: reduit ? "none" : `visibility 0s linear ${ouverte ? "0s" : ".3s"}`,
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 4 }}>
                    {entrees.map((e) => ligne(e.scene, numero(e)))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Sur téléphone, l'ouverture est EXACTEMENT la fermeture rejouée à l'envers :
  // même durée, et la courbe inversée dans le temps de `ease` (.25,.1,.25,1) —
  // P1' = (1−x2, 1−y2), P2' = (1−x1, 1−y1). Pas de fondu : la feuille glisse
  // seule, depuis hors de l'écran. Sur ordinateur, le panneau garde son entrée.
  const glissement = reduit
    ? `opacity .2s, visibility 0s linear ${ouvert ? "0s" : ".2s"}`
    : mobile
      ? ouvert
        ? "transform .35s cubic-bezier(.75,0,.75,.9), visibility 0s"
        : "transform .35s ease, visibility 0s linear .35s"
      : ouvert
        ? `${DEPLIAGE_OUVERTURE}, opacity .3s ease, visibility 0s`
        : `${DEPLIAGE_FERMETURE}, opacity .3s ease .1s, visibility 0s linear .4s`;

  return (
    <>
      {/* Le noir du changement de scène : sous le panneau, au-dessus du panorama. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: COULEURS.fond,
          opacity: noir ? 1 : 0,
          transition: `opacity ${duree}ms ease`,
          pointerEvents: noir ? "auto" : "none",
        }}
      />

      {/* Tout le reste, dans un calque. Il ne capte rien lui-même : seuls ses éléments répondent. */}
      <div
        ref={calque}
        style={{
          position: "absolute",
          inset: 0,
          // Au-dessus du dégradé bas du titre (`SceneTitle`), qui le teintait.
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
      {/* Repliée, ordinateur : le plan du domaine et un bouton pour ouvrir. */}
      {!mobile && racine.niveaux.length > 0 && (
        <div
          style={{
            ...carteFermee,
            left: MARGE_COIN,
            bottom: MARGE_COIN,
            opacity: ouvert ? 0 : 1,
            visibility: ouvert ? "hidden" : "visible",
            transition: "opacity .3s ease, visibility 0s",
          }}
        >
          {/* Le plan est une image, sans point ni zone (donc rien à focaliser) : il ne capte pas
              la souris, le clic revient au calque extérieur, qui ouvre comme le bouton. */}
          <div onClick={() => onOuvert(true)} style={{ cursor: "pointer" }}>
            <div aria-hidden style={{ pointerEvents: "none" }}>
              <SpacePlan
                espace={racine}
                assetBaseUrl={assetBaseUrl}
                sceneCourante={courant === racine ? sceneId : undefined}
                zones={[]}
                avecPoints={false}
                legende={t("navCaption")}
                libelleNiveaux={t("navLevel")}
                onChoisir={() => undefined}
              />
            </div>
          </div>
          <button
            ref={etiquette}
            type="button"
            data-sn-cle="etiquette"
            className="sn-bouton"
            data-sn-courant="false"
            aria-expanded={ouvert}
            aria-label={`${t("navOpen")} — ${nom(courant.titre)}, ${sceneCourante ? nom(sceneCourante.title) : ""}`}
            onClick={() => onOuvert(true)}
            style={boutonOuvrir}
          >
            <IconeCarte />
            {t("navOpen")}
          </button>
          <InvitePlan texte={t("navInvite")} ouvert={ouvert} />
        </div>
      )}

      {/* Repliée : « où suis-je » (téléphone, ou sans plan). */}
      {(mobile || racine.niveaux.length === 0) && (
      <button
        ref={etiquette}
        type="button"
        data-sn-cle="etiquette"
        className="sn-bouton sn-etiquette"
        aria-expanded={ouvert}
        aria-label={`${t("navOpen")} — ${nom(courant.titre)}, ${sceneCourante ? nom(sceneCourante.title) : ""}`}
        onClick={() => onOuvert(true)}
        style={{
          ...etiquetteStyle,
          ...(mobile
            ? {
                left: 0,
                right: 0,
                bottom: 0,
                minHeight: `calc(${ETIQUETTE_HAUTEUR_MOBILE}px + env(safe-area-inset-bottom))`,
                // 52 px de plus à droite : le bouton ⋯ du menu d'informations (handoff `design_handoff_credits`).
                padding: "8px calc(16px + 52px) calc(8px + env(safe-area-inset-bottom)) 12px",
                borderWidth: "1px 0 0",
                borderRadius: 0,
              }
            : { left: MARGE_COIN, bottom: MARGE_COIN, maxWidth: ETIQUETTE_LARGEUR }),
          opacity: ouvert ? 0 : 1,
          visibility: ouvert ? "hidden" : "visible",
          transition: "opacity .3s ease, visibility 0s",
        }}
      >
        {sceneCourante && <Vignette scene={sceneCourante} assetBaseUrl={assetBaseUrl} largeur={64} hauteur={40} />}
        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3, textAlign: "left" }}>
          <Surtitre>{nom(courant.titre)}</Surtitre>
          <span style={{ ...ellipse, fontFamily: POLICE_TITRE_SPLASH, fontSize: 19, lineHeight: 1.15 }}>
            {sceneCourante ? nom(sceneCourante.title) : ""}
          </span>
        </span>
        <IconeCarte />
        {!mobile && <InvitePlan texte={t("navInvite")} ouvert={ouvert} />}
      </button>
      )}

      {/* Téléphone : le ⋯ du menu d'informations, à droite de la barre, séparé par un filet. Il suit la
          barre (qui s'efface quand le panneau s'ouvre) ; les surcouches, elles, sont montées par `InfoMenu`. */}
      {mobile && (
        <div
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            height: `calc(${ETIQUETTE_HAUTEUR_MOBILE}px + env(safe-area-inset-bottom))`,
            paddingBottom: "env(safe-area-inset-bottom)",
            boxSizing: "border-box",
            display: "flex",
            opacity: ouvert ? 0 : 1,
            visibility: ouvert ? "hidden" : "visible",
            transition: "opacity .3s ease, visibility 0s",
          }}
        >
          <BoutonInfos variante="plus" habillage="barre" />
        </div>
      )}

      {/* Téléphone : le voile, qui referme au tap. */}
      {mobile && (
        <div
          aria-hidden
          className="sn-voile"
          onClick={() => onOuvert(false)}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(11,19,16,.45)",
            pointerEvents: ouvert ? "auto" : "none",
            opacity: ouvert ? 1 : 0,
            visibility: ouvert ? "visible" : "hidden",
            transition: ouvert ? "opacity .3s ease, visibility 0s" : "opacity .3s ease, visibility 0s linear .3s",
          }}
        />
      )}

      <section
        aria-label={t("navTitle")}
        aria-hidden={!ouvert}
        className="sn-panneau"
        style={{
          ...panneau,
          ...(mobile
            ? // Hauteur selon le contenu, plafonnée à 82 % de l'écran : une zone qui n'a qu'une
              // scène ne laisse pas un grand vide sous la carte, une zone chargée remplit le
              // plafond et sa liste défile.
              { left: 0, right: 0, bottom: 0, maxHeight: "82%" }
            : {
                left: MARGE_COIN,
                bottom: MARGE_COIN,
                // Ancré en bas, hauteur selon le contenu, plafonnée sous l'en-tête : la liste défile au-delà.
                // Fermé, il a la taille de la carte repliée : l'ouverture la déplie vers le haut
                // et l'élargit jusqu'à la largeur où le plan a sa taille finale.
                width: ouvert || reduit ? ETIQUETTE_LARGEUR : CARTE_LARGEUR,
                maxHeight: ouvert || reduit ? `calc(100% - ${MARGE_ENTETE + MARGE_COIN}px)` : CARTE_HAUTEUR,
                overflow: "hidden",
              }),
          // Tirée au doigt, la feuille suit le geste sans transition ; relâchée, elle
          // repart de là où elle est (vers le bas, ou en place).
          transform:
            tirer.decalage !== null
              ? `translateY(${tirer.decalage}px)`
              : ouvert || reduit || !mobile
                ? "none"
                : "translateY(104%)",
          opacity: ouvert || (mobile && !reduit) ? 1 : 0,
          visibility: ouvert ? "visible" : "hidden",
          transition: tirer.decalage !== null ? "none" : glissement,
        }}
      >
        {mobile && (
          <div aria-hidden {...tirer.poignee} style={zonePoignee}>
            <span style={poignee} />
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <Surtitre>{t("navTitle")}</Surtitre>
          <button
            ref={fermer}
            type="button"
            data-sn-cle="fermer"
            className="sn-bouton"
            data-sn-courant="false"
            aria-label={t("navClose")}
            onClick={() => {
              onOuvert(false);
              etiquette.current?.focus();
            }}
            style={boutonFermer}
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        {/* Ordinateur : tout défile ensemble. Téléphone : le plan reste fixe, seules les scènes
            défilent (`ListeDefilante`, avec ses chevrons). */}
        <div className="sn-defile" style={{ flex: mobile ? "1 1 auto" : 1, minHeight: 0, overflowY: mobile ? "hidden" : "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          {racine.niveaux.length > 0 && (
            <SpacePlan
              espace={racine}
              assetBaseUrl={assetBaseUrl}
              sceneCourante={courant === racine ? sceneId : undefined}
              zones={espaces
                .filter((e) => e.zone)
                .map((e) => ({
                  espace: e,
                  // Ordinateur : la zone ouvre sa première scène. Téléphone : elle change la liste
                  // du dessous, la feuille reste ouverte — fermer à chaque tap interdirait de
                  // parcourir les scènes d'une zone avant d'en choisir une.
                  ici: mobile ? e === espaceVu : e === courant,
                  ouvrir: mobile
                    ? () => {
                        setVu(e.id);
                        // Comme à l'arrivée dans une scène : la sous-zone de la scène par défaut
                        // de la zone est dépliée, pas celle d'où l'on vient.
                        const defaut = e.entree ?? e.scenes[0]?.scene.id;
                        etatNavigation.setSousZone(e.scenes.find((x) => x.scene.id === defaut)?.sousZone ?? null);
                      }
                    : () => {
                        const arrivee = e.entree ?? e.scenes[0]?.scene.id;
                        if (arrivee) choisir(arrivee);
                      },
                }))}
              avecPoints={false}
              nomZoneSelectionnee={mobile}
              legende={t("navCaption")}
              libelleNiveaux={t("navLevel")}
              onChoisir={choisir}
            />
          )}
          <div style={{ textAlign: "center" }}>
            <div style={{ ...ellipse, fontFamily: POLICE_TITRE_SPLASH, fontSize: 23 }}>
              {nom((mobile ? espaceVu : courant).titre)}
            </div>
          </div>
          {mobile ? (
            // Le plan reste fixe ; seules les scènes défilent, chevrons à l'appui.
            <ListeDefilante key={espaceVu.id}>{listeScenes(espaceVu)}</ListeDefilante>
          ) : (
            listeScenes(courant)
          )}
        </div>
      </section>
      </div>
    </>
  );
}

/**
 * Fermer la feuille en la tirant vers le bas par sa poignée. La feuille suit le
 * doigt (jamais au-dessus de sa place) ; au relâcher, elle se ferme si elle est
 * descendue de plus d'un quart de sa hauteur, ou si le geste était un coup vif
 * vers le bas ; sinon elle remonte. Le geste est capté sur la poignée seule :
 * la liste, dessous, garde son défilement.
 */
function useTirerPourFermer(fermer: () => void) {
  const [decalage, setDecalage] = useState<number | null>(null);
  const depart = useRef<{ y: number; t: number; hauteur: number } | null>(null);

  const poignee = {
    onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      const feuille = event.currentTarget.parentElement;
      depart.current = { y: event.clientY, t: performance.now(), hauteur: feuille?.offsetHeight ?? 1 };
      setDecalage(0);
    },
    onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!depart.current) return;
      setDecalage(Math.max(0, event.clientY - depart.current.y));
    },
    onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => {
      const d = depart.current;
      depart.current = null;
      setDecalage(null);
      if (!d) return;
      const dy = event.clientY - d.y;
      const vitesse = dy / Math.max(1, performance.now() - d.t); // px/ms
      if (dy > d.hauteur / 4 || (dy > 20 && vitesse > 0.5)) fermer();
    },
    onPointerCancel: () => {
      depart.current = null;
      setDecalage(null);
    },
  };

  return { decalage, poignee };
}

/** La vignette d'une scène : la face avant de son preview, recadrée au centre. */
function Vignette({
  scene,
  assetBaseUrl,
  largeur,
  hauteur,
}: {
  scene: Scene;
  assetBaseUrl: string;
  largeur: number;
  hauteur: number;
}) {
  const source = scene.visuals[0]?.version.source;
  const cle = source?.type === "cubemap" ? source.pyramid.preview?.key : undefined;
  return (
    <span
      aria-hidden
      style={{
        flex: "none",
        width: largeur,
        height: hauteur,
        borderRadius: 1,
        background: "linear-gradient(135deg, #2b3340, #161a20)",
        // Six faces empilées, la face avant en haut : on la cale au milieu du cadre.
        ...(cle
          ? {
              backgroundImage: `url("${resolveAssetUrl(assetBaseUrl, cle)}")`,
              backgroundSize: `${largeur}px ${largeur * 6}px`,
              backgroundPosition: `0 ${-(largeur - hauteur) / 2}px`,
              backgroundRepeat: "no-repeat",
            }
          : null),
      }}
    />
  );
}

function Surtitre({ children }: { children: ReactNode }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <span aria-hidden style={losange} />
      <span style={{ ...surtitreTexte, ...ellipse }}>{children}</span>
    </span>
  );
}

function IconeCarte() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: "none", color: COULEURS.sauge }}>
      <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" />
      <path d="M9 4v14M15 6v14" />
    </svg>
  );
}

/* --- styles (valeurs du handoff) ------------------------------------------ */

const ellipse: CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const losange: CSSProperties = {
  width: 6,
  height: 6,
  transform: "rotate(45deg)",
  background: COULEURS.sauge,
  flex: "none",
};

const surtitreTexte: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".16em",
  textTransform: "uppercase",
  color: "#E3D9C8",
};

const fondPanneau: CSSProperties = {
  background: "rgba(16,26,23,.8)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(246,238,227,.22)",
  borderRadius: 2,
  color: COULEURS.creme,
  fontFamily: POLICE_UI_SPLASH,
};

/** La place (coin bas gauche, ou barre basse sur téléphone) dépend de l'appareil : voir le rendu. */
const etiquetteStyle: CSSProperties = {
  ...fondPanneau,
  appearance: "none",
  position: "absolute",
  minHeight: 60,
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "8px 14px 8px 8px",
  cursor: "pointer",
  pointerEvents: "auto",
  boxShadow: "0 10px 30px rgba(8,14,12,.35)",
};

/** La carte repliée (ordinateur) : le plan au-dessus, le bouton d'ouverture dessous. */
const carteFermee: CSSProperties = {
  ...fondPanneau,
  position: "absolute",
  boxSizing: "border-box",
  width: 260,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 8,
  pointerEvents: "auto",
  boxShadow: "0 10px 30px rgba(8,14,12,.35)",
};

const boutonOuvrir: CSSProperties = {
  appearance: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "8px 10px",
  border: "1px solid rgba(246,238,227,.22)",
  background: "rgba(246,238,227,.06)",
  color: COULEURS.creme,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: ".12em",
  textTransform: "uppercase",
  cursor: "pointer",
};

const panneau: CSSProperties = {
  ...fondPanneau,
  position: "absolute",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 14,
  pointerEvents: "auto",
  boxShadow: "0 10px 30px rgba(8,14,12,.35)",
};

/** Une bande pleine largeur autour du trait : la cible du pouce, pas le trait seul. */
const zonePoignee: CSSProperties = {
  margin: "-14px -14px -6px",
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  touchAction: "none",
  cursor: "grab",
};

const poignee: CSSProperties = {
  width: 40,
  height: 4,
  borderRadius: 2,
  background: "rgba(246,238,227,.35)",
};

const boutonFermer: CSSProperties = {
  appearance: "none",
  flex: "none",
  width: 44,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  borderRadius: 2,
  color: COULEURS.creme,
  cursor: "pointer",
};

/** En-tête d'une sous-zone : un surtitre cliquable, qui replie ses scènes. */
const enteteSousZone: CSSProperties = {
  appearance: "none",
  minHeight: 44,
  marginTop: 6,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 10px",
  background: "transparent",
  border: "none",
  borderBottom: "1px solid rgba(246,238,227,.12)",
  color: COULEURS.creme,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: ".16em",
  textTransform: "uppercase",
  cursor: "pointer",
};

const ligneStyle: CSSProperties = {
  appearance: "none",
  minHeight: 52,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "6px 10px 6px 6px",
  border: "1px solid",
  borderRadius: 2,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 14,
  textAlign: "left",
  cursor: "pointer",
};
